import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Anchor } from '@/components/atoms/anchor';
import { StyledInput } from '@/components/atoms/inputs/styled';
import { Text } from '@/components/atoms/text';
import { LoadingButton } from '@/components/atoms/Button';
import { AppRouters } from '@/utils/constants/routers';
import { Container } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Background,
  FormContent,
  FormContainer,
  FormTitle,
  StyledAnchor,
  StyledText,
} from './styles';
import { resetPassword } from '@/lib/auth';
import { PublicRoute } from '@/components/organisms/route/public';
import { useAlert } from '@/hooks/useAlert';
import { Alert } from '@/components/molecules/alert';
import { Field } from '@/components/atoms/field';
import { Label } from '@/components/atoms/label';
import { useTranslation } from '@/hooks/useTranslation';
import { LanguageSelector } from '@/components/molecules/languageSelector';

const initialValues = {
  email: '',
};

export default function ForgotPasswordPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loadingButton, setLoadingButton] = useState(false);

  const { alert, showAlert, handleCloseAlert } = useAlert();

  const schema = Yup.object().shape({
    email: Yup.string()
      .required(t('messages.error.error.email_required'))
      .email(t('messages.error.error.invalid_email')),
  });

  const handleSubmit = async ({ email }: typeof initialValues) => {
    try {
      setLoadingButton(true);
      await resetPassword(email);
      showAlert('success', t('messages.success.message.reset_password'));
      await new Promise((resolve) => setTimeout(resolve, 1000));
      navigate(AppRouters.login);
    } catch (error) {
      showAlert('error', t('messages.error.error.email_incorrect'));
    } finally {
      setLoadingButton(false);
    }
  };

  useEffect(() => {
    document.title = 'KYT-e | GBF - Forgot Password';
  }, []);

  return (
    <PublicRoute>
      <Background>
        <Container>
          <FormContent>
            <FormTitle>
              <Text variant="h4">{t('page.forgotPassword.forgotPassword.title')}</Text>

              <Text variant="body1" secondary sx={StyledText}>
                {t('page.forgotPassword.forgotPassword.description')}
              </Text>
            </FormTitle>

            <Formik
              initialValues={initialValues}
              onSubmit={(values) => handleSubmit(values)}
              validationSchema={schema}
            >
              {({ errors, handleChange }) => (
                <Form>
                  <FormContainer>
                    <Field>
                      <Label text={t('label.label.register_email')} htmlFor="email" />
                      <StyledInput
                        id="email"
                        name="email"
                        margin="none"
                        placeholder="example@email.com"
                        type="email"
                        onChange={handleChange}
                        helperText={errors.email}
                        fullWidth
                        error={Boolean(errors.email)}
                      />
                    </Field>

                    <LoadingButton
                      data-testid="forgot-password-button"
                      loading={loadingButton}
                      variant="contained"
                      size="large"
                      type="submit"
                      sx={{ height: '44px' }}
                    >
                      {t('button.button.send_register')}
                    </LoadingButton>
                  </FormContainer>
                </Form>
              )}
            </Formik>

            <StyledAnchor>
              <Anchor
                label={t('button.button.goToLogin')}
                href={AppRouters.login}
                color={theme.palette.info.main}
              />
            </StyledAnchor>

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '24px',
              }}
            >
              <LanguageSelector />
            </div>
          </FormContent>
        </Container>
      </Background>

      {alert.open && (
        <Alert
          open={alert.open}
          severity={alert.severity}
          message={alert.message}
          autoHideDuration={5000}
          onClose={handleCloseAlert}
          dataTestId="forgot-password-alert"
        />
      )}
    </PublicRoute>
  );
}
